import { Children, forwardRef, memo } from 'react';

import { getDataTestId } from '@/js/libs/utils/get-data-test-id/index.ts';

import { InfoTooltipIcon } from '../../../../Feedback/InfoTooltipIcon/index.tsx';
import { useExpandCollapse } from '../../hooks/index.ts';
import { ActionsWrapper, ContentWrapper, CollapsibleWrapper, SubTitle, TitleWrapper, Title, SubSectionWrapper } from './styled.ts';
import type { SubSectionProps } from './types.ts';

export const SubSection = memo(
  forwardRef<HTMLDivElement, SubSectionProps>(function SubSection(props, ref) {
    const {
      children,
      id,
      className,
      dataTestId,
      title,
      subTitle,
      tooltip,
      actions,
      horizontal,
      noPadding,
      height,
      expanded,
      contentStyles,
      contentOverflow = 'auto',
    } = props;
    const { getCollapseProps } = useExpandCollapse(expanded);
    const hasFixedHeight = !!height;
    const childCount = horizontal ? Children.count(children) : 0;

    return (
      <SubSectionWrapper id={id} className={className} height={height} ref={ref} data-testid={dataTestId}>
        {title && (
          <TitleWrapper data-testid={getDataTestId(title, 'title', 'section-subsection')}>
            <Title>
              {title}
              {tooltip && <InfoTooltipIcon tooltip={tooltip} size={20} dataTestId='section-subsection-title-tooltip' />}
            </Title>
            <ActionsWrapper>{actions}</ActionsWrapper>
          </TitleWrapper>
        )}
        {subTitle && <SubTitle>{subTitle}</SubTitle>}
        {children && (
          <CollapsibleWrapper
            {...getCollapseProps()}
            hasFixedHeight={hasFixedHeight}
            data-testid={getDataTestId(title ?? 'title', 'content', 'section-subsection')}
            overflow={contentOverflow}>
            <ContentWrapper
              withoutTitle={!title}
              customStyles={contentStyles}
              noPadding={horizontal ? true : noPadding}
              horizontal={horizontal}
              childCount={childCount}
              hasFixedHeight={hasFixedHeight}>
              {children}
            </ContentWrapper>
          </CollapsibleWrapper>
        )}
      </SubSectionWrapper>
    );
  }),
);

export type { SubSectionProps };
